import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useBillingReconciliationBacAccounts } from '../../hooks/api/useBillingReconciliationHooks.js';

const exactBac = (value) => /^\d{6}$/.test(String(value || '').trim());

const BacAccountLookupDialog = ({ open, runId, onClose, onSelect }) => {
  const [bacInput, setBacInput] = useState('');
  const [submittedBac, setSubmittedBac] = useState('');
  const accountsQuery = useBillingReconciliationBacAccounts(
    { runId, bac: submittedBac, limit: 100 },
    { enabled: Boolean(open && runId && submittedBac) },
  );

  useEffect(() => {
    if (open) return;
    setBacInput('');
    setSubmittedBac('');
  }, [open]);

  const search = () => {
    const bac = String(bacInput || '').trim();
    if (exactBac(bac)) setSubmittedBac(bac);
  };

  const accounts = Array.isArray(accountsQuery.data) ? accountsQuery.data : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="bac-account-lookup-title">
      <DialogTitle id="bac-account-lookup-title">Look up Salesforce account by BAC</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              autoFocus
              fullWidth
              label="BAC"
              value={bacInput}
              onChange={(event) => setBacInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && exactBac(bacInput)) search();
              }}
              error={Boolean(bacInput) && !exactBac(bacInput)}
              helperText={bacInput && !exactBac(bacInput) ? 'Enter a six-digit BAC.' : 'Search runs only when you submit an exact BAC.'}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            />
            <Button variant="contained" onClick={search} disabled={!exactBac(bacInput) || accountsQuery.isFetching} sx={{ minWidth: 96, mt: { sm: 0.5 } }}>
              Search
            </Button>
          </Stack>
          {accountsQuery.isError && <Alert severity="error">Salesforce accounts could not be loaded for this BAC.</Alert>}
          {accountsQuery.isFetching && <Alert severity="info">Searching Salesforce accounts...</Alert>}
          {submittedBac && !accountsQuery.isFetching && !accountsQuery.isError && accounts.length === 0 && (
            <Alert severity="warning">No Salesforce accounts were found for BAC {submittedBac}.</Alert>
          )}
          {accounts.length > 0 && (
            <List disablePadding aria-label={`Salesforce accounts for BAC ${submittedBac}`}>
              {accounts.map((account, index) => {
                const salesforceAccountId = account.salesforceId || account.salesforceAccountId || account.id || '';
                const peopleSoftId = account.peopleSoftId || account.peoplesoftId || '';
                const name = account.name || account.salesforceAccountName || 'Salesforce account';
                return (
                  <ListItemButton
                    key={`${salesforceAccountId || name}-${index}`}
                    divider={index < accounts.length - 1}
                    onClick={() => onSelect({
                      ...account,
                      bac: submittedBac,
                      id: salesforceAccountId || name,
                      salesforceAccountId,
                      salesforceAccountName: name,
                      peopleSoftId,
                    })}
                  >
                    <ListItemText
                      primary={name}
                      secondary={(
                        <Stack component="span" spacing={0.25}>
                          <Typography component="span" variant="caption">Salesforce: {salesforceAccountId || '--'}</Typography>
                          <Typography component="span" variant="caption">PeopleSoft: {peopleSoftId || 'Missing'}</Typography>
                        </Stack>
                      )}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
    </Dialog>
  );
};

export default BacAccountLookupDialog;
